/**
 * In-viewport handles for the splat editor:
 *  - a transform pivot (move / rotate / scale) whose drag previews on the GPU
 *    and commits as one undoable palette transform on release,
 *  - sphere / box selection volumes (wireframe + gizmo) whose world matrix maps
 *    the unit shape to world space, as SplatEditSystem.selectSphere/Box expect,
 *  - point markers for the measure and orient tools.
 *
 * Uses PlayCanvas transform gizmos on their own layer. The editor passes
 * interaction callbacks so camera input is suspended only while a handle is
 * actually being dragged.
 */
import * as pc from 'playcanvas';
import type { SplatEditSystem } from '../SplatEditSystem';

export type GizmoMode = 'translate' | 'rotate' | 'scale';
export type ShapeKind = 'sphere' | 'box';

export interface EditGizmoHooks {
    onInteractStart?: () => void;
    onInteractEnd?: () => void;
    /** Shape moved/resized (UI can refresh labels). */
    onShapeChange?: () => void;
    /** The transform pivot moved, or a drag of it progressed (UI shows live values). */
    onPivotChange?: (info: PivotInfo) => void;
}

export interface PivotInfo {
    /** World position of the pivot. */
    position: pc.Vec3;
    /**
     * The drag in progress relative to its start (null when idle): translation
     * in world units, rotation in degrees and per-axis scale, in the gizmo's
     * space (world, or the pivot's own axes in local space).
     */
    drag: { translate: pc.Vec3; rotateDeg: pc.Vec3; scale: pc.Vec3 } | null;
}

const SHAPE_COLOR = new pc.Color(0.3, 0.7, 1, 1);
const MARKER_COLOR = new pc.Color(1, 0.85, 0.1, 1);
const BOUNDS_COLOR = new pc.Color(1, 1, 1, 0.8);

export class EditGizmos {
    private readonly app: pc.AppBase;
    private readonly camera: pc.CameraComponent;
    private readonly system: SplatEditSystem;
    private readonly hooks: EditGizmoHooks;
    private readonly layer: pc.Layer;
    private readonly gizmos: Record<GizmoMode, pc.TransformGizmo>;
    private active: pc.TransformGizmo | null = null;
    private readonly pivot: pc.Entity;
    private shape: pc.Entity | null = null;
    private shapeKind: ShapeKind | null = null;
    private target: 'pivot' | 'shape' | null = null;
    private dragStart = new pc.Mat4();
    private dragging = false;
    private markers: pc.Vec3[] = [];
    private markersClosed = false;
    private readonly onUpdate: () => void;
    private space: 'world' | 'local' = 'world';
    private showBounds = false;

    constructor(app: pc.AppBase, camera: pc.CameraComponent, system: SplatEditSystem, hooks: EditGizmoHooks = {}) {
        this.app = app;
        this.camera = camera;
        this.system = system;
        this.hooks = hooks;
        this.layer = (pc.Gizmo as unknown as { createLayer: (app: pc.AppBase, name?: string) => pc.Layer }).createLayer(app, 'SplatEditGizmo');
        const make = (Ctor: typeof pc.TranslateGizmo | typeof pc.RotateGizmo | typeof pc.ScaleGizmo) => {
            const g = new (Ctor as unknown as new (camera: pc.CameraComponent, layer: pc.Layer) => pc.TransformGizmo)(camera, this.layer);
            this._wire(g);
            return g;
        };
        this.gizmos = {
            translate: make(pc.TranslateGizmo),
            rotate: make(pc.RotateGizmo),
            scale: make(pc.ScaleGizmo),
        };
        (this.gizmos.scale as unknown as { uniform?: boolean }).uniform = false;
        this.pivot = new pc.Entity('splat-edit-pivot');
        app.root.addChild(this.pivot);
        this.onUpdate = () => this._draw();
        app.on('update', this.onUpdate);
    }

    // ─── Transform tool ─────────────────────────────────────

    /**
     * Show the transform gizmo on the selection (or the whole splat). Null hides it.
     * `keepPivot` leaves the pivot where it is (e.g. set by the orient tool).
     */
    setTransformMode(mode: GizmoMode | null, keepPivot = false): void {
        if (!mode) {
            if (this.target === 'pivot') this._detach();
            return;
        }
        if (!keepPivot) this.placePivot();
        this._attach(mode, this.pivot, 'pivot');
    }

    /** Move the pivot to the selection's center (or the splat's center). */
    placePivot(at?: pc.Vec3): void {
        const center = at ?? this.system.getSelectionCenter() ?? this.system.getBounds(false)?.center ?? new pc.Vec3();
        this.pivot.setPosition(center);
        this.pivot.setRotation(this.space === 'local' && this.system.renderer ? this.system.renderer.entity.getRotation() : pc.Quat.IDENTITY);
        this.pivot.setLocalScale(1, 1, 1);
        this._refresh();
        this._emitPivot();
    }

    /** World position of the transform pivot. */
    get pivotPosition(): pc.Vec3 {
        return this.pivot.getPosition().clone();
    }

    /**
     * A typed transform about the pivot, as one undo step: move the pivot (and
     * the selection, or the whole splat) to a world point, or rotate / scale
     * about the pivot along the gizmo's axes (world, or the pivot's own in
     * local space).
     */
    applyNumeric(t: { moveTo?: pc.Vec3; rotateDeg?: [number, number, number]; scale?: [number, number, number] }): void {
        if (this.dragging) return;
        const p = this.pivot.getPosition().clone();
        if (t.moveTo) {
            const m = new pc.Mat4().setTranslate(t.moveTo.x - p.x, t.moveTo.y - p.y, t.moveTo.z - p.z);
            this.system.transformSelection(m, 'Move');
            this.pivot.setPosition(t.moveTo);
        } else {
            const frame = this.space === 'local' ? this.pivot.getRotation().clone() : new pc.Quat();
            const r = t.rotateDeg ? new pc.Quat().setFromEulerAngles(t.rotateDeg[0], t.rotateDeg[1], t.rotateDeg[2]) : new pc.Quat();
            const sc = t.scale ? new pc.Vec3(t.scale[0], t.scale[1], t.scale[2]) : new pc.Vec3(1, 1, 1);
            // T(p) · F · R · S · F⁻¹ · T(−p)
            const F = new pc.Mat4().setTRS(pc.Vec3.ZERO, frame, pc.Vec3.ONE);
            const Finv = F.clone().invert();
            const m = new pc.Mat4().setTranslate(p.x, p.y, p.z)
                .mul(F)
                .mul(new pc.Mat4().setTRS(pc.Vec3.ZERO, r, sc))
                .mul(Finv)
                .mul(new pc.Mat4().setTranslate(-p.x, -p.y, -p.z));
            this.system.transformSelection(m, t.rotateDeg ? 'Rotate' : 'Scale');
            if (t.rotateDeg) {
                // turn the pivot with the selection, as a gizmo drag does
                const worldDelta = new pc.Quat().mul2(frame, r).mul(frame.clone().invert());
                this.pivot.setRotation(new pc.Quat().mul2(worldDelta, this.pivot.getRotation()));
            }
        }
        this._refresh();
        this._emitPivot();
    }

    private _emitPivot(): void {
        if (!this.hooks.onPivotChange) return;
        let drag: PivotInfo['drag'] = null;
        if (this.dragging && this.target === 'pivot') {
            const start = new pc.Vec3(), startScale = new pc.Vec3();
            this.dragStart.getTranslation(start);
            this.dragStart.getScale(startScale);
            const startRot = new pc.Quat().setFromMat4(this.dragStart);
            const rot = this.pivot.getRotation();
            const delta = this.space === 'local'
                ? new pc.Quat().mul2(startRot.clone().invert(), rot)
                : new pc.Quat().mul2(rot, startRot.clone().invert());
            const s = this.pivot.getLocalScale();
            drag = {
                translate: new pc.Vec3().sub2(this.pivot.getPosition(), start),
                rotateDeg: delta.getEulerAngles(),
                scale: new pc.Vec3(s.x / (startScale.x || 1), s.y / (startScale.y || 1), s.z / (startScale.z || 1)),
            };
        }
        this.hooks.onPivotChange({ position: this.pivot.getPosition().clone(), drag });
    }

    setCoordSpace(space: 'world' | 'local'): void {
        this.space = space;
        for (const g of Object.values(this.gizmos)) g.coordSpace = space;
        if (this.target === 'pivot') this.placePivot(this.pivot.getPosition().clone());
    }

    setSnap(on: boolean, increments = { translate: 0.1, rotate: 15, scale: 0.1 }): void {
        for (const [mode, g] of Object.entries(this.gizmos) as [GizmoMode, pc.TransformGizmo][]) {
            g.snap = on;
            g.snapIncrement = increments[mode];
        }
    }

    // ─── Volume tools ──────────────────────────────────────

    /** Show a sphere/box selection volume (null removes it). */
    setShape(kind: ShapeKind | null, mode: GizmoMode = 'translate'): void {
        if (!kind) {
            if (this.target === 'shape') this._detach();
            this.shape?.destroy();
            this.shape = null;
            this.shapeKind = null;
            this._refresh();
            return;
        }
        if (!this.shape || this.shapeKind !== kind) {
            const prevMatrix = this.shape?.getWorldTransform().clone();
            this.shape?.destroy();
            this.shape = new pc.Entity(`splat-edit-${kind}`);
            this.app.root.addChild(this.shape);
            this.shapeKind = kind;
            if (prevMatrix) {
                const p = new pc.Vec3(), r = new pc.Quat(), s = new pc.Vec3();
                prevMatrix.getTranslation(p); r.setFromMat4(prevMatrix); prevMatrix.getScale(s);
                this.shape.setPosition(p); this.shape.setRotation(r); this.shape.setLocalScale(s);
            } else {
                this._placeShapeDefault();
            }
        }
        this._attach(mode, this.shape, 'shape');
    }

    /** Uniform size of the volume (diameter / side), or null without a volume. */
    getShapeSize(): number | null {
        if (!this.shape) return null;
        const s = this.shape.getLocalScale();
        return (s.x + s.y + s.z) / 3;
    }

    setShapeSize(size: number): void {
        if (!this.shape || !(size > 0)) return;
        this.shape.setLocalScale(size, size, size);
        this.hooks.onShapeChange?.();
        this._refresh();
    }

    setShapeMode(mode: GizmoMode): void {
        if (this.shape) this._attach(mode, this.shape, 'shape');
    }

    /** World matrix mapping the unit shape (diameter/side 1) to world space. */
    getShapeMatrix(): Float32Array | null {
        return this.shape ? Float32Array.from(this.shape.getWorldTransform().data) : null;
    }

    /** Re-center the volume on the selection (or the splat), sized to fit it. */
    fitShapeToSelection(): void {
        if (this.shape) this._placeShapeDefault();
    }

    /** Draw the selection's (or the splat's) bounding box. */
    setShowBounds(on: boolean): void {
        this.showBounds = on;
        this._refresh();
    }

    /** Place the pivot at the splat's origin (the edited entity's position). */
    placePivotAtOrigin(): void {
        const e = this.system.renderer?.entity;
        this.placePivot(e ? e.getPosition().clone() : new pc.Vec3());
    }

    // ─── Markers (measure / orient) ─────────────────────────

    setMarkers(points: pc.Vec3[], closed = false): void {
        this.markers = points.map((p) => p.clone());
        this.markersClosed = closed;
        this._refresh();
    }

    get isDragging(): boolean {
        return this.dragging;
    }

    destroy(): void {
        this.app.off('update', this.onUpdate);
        for (const g of Object.values(this.gizmos)) {
            g.detach();
            g.destroy();
        }
        this.pivot.destroy();
        this.shape?.destroy();
        this.camera.layers = this.camera.layers.filter((id) => id !== this.layer.id);
        this.app.scene.layers.remove(this.layer);
        this._refresh();
    }

    // ─── Internals ─────────────────────────────────────────

    private _wire(g: pc.TransformGizmo): void {
        g.on('pointer:down', (_x: number, _y: number, meshInstance: unknown) => {
            if (meshInstance) this.hooks.onInteractStart?.();
        });
        g.on('pointer:up', () => {
            if (!this.dragging) requestAnimationFrame(() => this.hooks.onInteractEnd?.());
        });
        g.on('transform:start', () => {
            this.dragging = true;
            if (this.target === 'pivot') this.dragStart.copy(this.pivot.getWorldTransform());
        });
        g.on('transform:move', () => {
            if (this.target === 'pivot') {
                this.system.setPreviewTransform(this._pivotDelta());
                this._emitPivot();
            } else {
                this.hooks.onShapeChange?.();
            }
            this._refresh();
        });
        g.on('transform:end', () => {
            this.dragging = false;
            if (this.target === 'pivot') {
                const delta = this._pivotDelta();
                this.system.setPreviewTransform(null);
                this.system.transformSelection(delta, this.active === this.gizmos.rotate ? 'Rotate' : this.active === this.gizmos.scale ? 'Scale' : 'Move');
                // keep the pivot where it was dropped, but back at unit scale
                this.pivot.setLocalScale(1, 1, 1);
                this._emitPivot();
            } else {
                this.hooks.onShapeChange?.();
            }
            requestAnimationFrame(() => this.hooks.onInteractEnd?.());
            this._refresh();
        });
    }

    private _pivotDelta(): pc.Mat4 {
        const start = this.dragStart.clone().invert();
        return new pc.Mat4().mul2(this.pivot.getWorldTransform(), start);
    }

    private _attach(mode: GizmoMode, entity: pc.Entity, target: 'pivot' | 'shape'): void {
        const g = this.gizmos[mode];
        if (this.active && this.active !== g) this.active.detach();
        this.active = g;
        this.target = target;
        g.coordSpace = target === 'shape' ? 'local' : this.space;
        g.attach([entity]);
        this._refresh();
    }

    private _detach(): void {
        this.active?.detach();
        this.active = null;
        this.target = null;
        this._refresh();
    }

    private _placeShapeDefault(): void {
        if (!this.shape) return;
        const b = this.system.getBounds(true) ?? this.system.getBounds(false);
        if (b) {
            const size = b.max.clone().sub(b.min);
            const s = Math.max(size.x, size.y, size.z) * (this.system.hasSelection() ? 1.1 : 0.35) || 1;
            this.shape.setPosition(b.center);
            this.shape.setRotation(pc.Quat.IDENTITY);
            this.shape.setLocalScale(s, s, s);
        } else {
            this.shape.setPosition(0, 0, 0);
            this.shape.setLocalScale(1, 1, 1);
        }
        this.hooks.onShapeChange?.();
        this._refresh();
    }

    private _draw(): void {
        const app = this.app as pc.AppBase & { drawLine: (a: pc.Vec3, b: pc.Vec3, c: pc.Color, depthTest?: boolean, layer?: pc.Layer) => void };
        if (this.shape && this.shapeKind) {
            const m = this.shape.getWorldTransform();
            if (this.shapeKind === 'box') {
                const c = [-0.5, 0.5];
                const corners: pc.Vec3[] = [];
                for (const x of c) for (const y of c) for (const z of c) corners.push(m.transformPoint(new pc.Vec3(x, y, z)));
                const edges = [[0, 1], [2, 3], [4, 5], [6, 7], [0, 2], [1, 3], [4, 6], [5, 7], [0, 4], [1, 5], [2, 6], [3, 7]];
                for (const [a, b] of edges) app.drawLine(corners[a], corners[b], SHAPE_COLOR, false);
            } else {
                const seg = 48;
                for (let axis = 0; axis < 3; axis++) {
                    let prev: pc.Vec3 | null = null;
                    for (let k = 0; k <= seg; k++) {
                        const t = (k / seg) * Math.PI * 2;
                        const u = Math.cos(t) * 0.5, v = Math.sin(t) * 0.5;
                        const p = axis === 0 ? new pc.Vec3(0, u, v) : axis === 1 ? new pc.Vec3(u, 0, v) : new pc.Vec3(u, v, 0);
                        const w = m.transformPoint(p);
                        if (prev) app.drawLine(prev, w, SHAPE_COLOR, false);
                        prev = w;
                    }
                }
            }
        }
        if (this.showBounds) {
            const b = this.system.getBoundsCached();
            if (b) {
                const { min, max } = b;
                const corners: pc.Vec3[] = [];
                for (const x of [min.x, max.x]) for (const y of [min.y, max.y]) for (const z of [min.z, max.z]) corners.push(new pc.Vec3(x, y, z));
                const edges = [[0, 1], [2, 3], [4, 5], [6, 7], [0, 2], [1, 3], [4, 6], [5, 7], [0, 4], [1, 5], [2, 6], [3, 7]];
                for (const [a, c] of edges) app.drawLine(corners[a], corners[c], BOUNDS_COLOR, false);
            }
        }
        const pts = this.markers;
        if (pts.length) {
            const camPos = this.camera.entity.getPosition();
            for (const p of pts) {
                // small screen-constant cross
                const s = p.distance(camPos) * 0.012;
                app.drawLine(new pc.Vec3(p.x - s, p.y, p.z), new pc.Vec3(p.x + s, p.y, p.z), MARKER_COLOR, false);
                app.drawLine(new pc.Vec3(p.x, p.y - s, p.z), new pc.Vec3(p.x, p.y + s, p.z), MARKER_COLOR, false);
                app.drawLine(new pc.Vec3(p.x, p.y, p.z - s), new pc.Vec3(p.x, p.y, p.z + s), MARKER_COLOR, false);
            }
            for (let k = 1; k < pts.length; k++) app.drawLine(pts[k - 1], pts[k], MARKER_COLOR, false);
            if (this.markersClosed && pts.length > 2) app.drawLine(pts[pts.length - 1], pts[0], MARKER_COLOR, false);
        }
        if ((this.shape || pts.length || this.showBounds) && this.system.renderer) this.system.renderer.requestRender();
    }

    private _refresh(): void {
        this.system.renderer?.requestRender();
    }
}
